/**
 * Alerta de ACOMPANHANTE ao marcar o paciente como "próximo a ser atendido":
 * pergunta à secretária se o paciente vai entrar acompanhado.
 *
 *  - "Não, entra sozinho" → fecha (e limpa acompanhante já informado, se houver).
 *  - "Sim" → busca do acompanhante entre os PACIENTES CADASTRADOS
 *    (AccompanistSelect — não aceita texto livre).
 *  - Acompanhante sem cadastro → "Cadastrar acompanhante" abre o cadastro
 *    rápido (PatientFormModal em modo `requireFullIdentity`: nome completo,
 *    CPF e telefone são obrigatórios) já com o nome buscado preenchido; ao
 *    salvar, o recém-criado é vinculado automaticamente.
 *
 * O vínculo é gravado no AGENDAMENTO (accompanistId/Name/Cpf) — o mesmo campo
 * do "Novo Agendamento" — e daí flui pro "acompanhante do dia" da sessão
 * quando a ficha do paciente abre (PatientDetailPage).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserX, Loader2, AlertCircle, Star } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { AccompanistSelect } from '@/components/patients/AccompanistSelect';
import { PatientFormModal } from '@/pages/patients/PatientFormModal';
import { AppointmentService } from '@/services/firebase/appointments';
import { PatientService } from '@/services/firebase/patients';
import { decryptData } from '@/utils/crypto';
import type { Appointment, Patient } from '@/types/models';

export interface NextUpAccompanistModalProps {
  isOpen: boolean;
  clinicId: string | null | undefined;
  /** Agendamento recém-marcado como próximo. */
  appointment: Pick<
    Appointment,
    'id' | 'patientId' | 'patientName' | 'accompanistId' | 'accompanistName'
  > | null;
  onClose: () => void;
  /** Avisa o pai que o agendamento mudou — pra refetch onde não há listener
   *  em tempo real (ex.: grade da AgendaPage). */
  onSaved?: () => void;
  /** Disparado quando a secretária RESPONDE a pergunta (acompanhante
   *  vinculado, mantido ou "entra sozinho") — diferente do onClose, que
   *  também dispara ao fechar sem responder (X/backdrop). O "Avisar médico"
   *  usa isto pra continuar o envio só depois da resposta. */
  onDecided?: () => void;
  /**
   * Contexto de abertura — muda só o texto:
   *  - 'nextUp' (default): alerta ao marcar como próximo ("foi marcado…").
   *  - 'edit': edição avulsa do acompanhante do dia (painel lateral da
   *    agenda), sem mencionar "próximo a ser atendido".
   */
  context?: 'nextUp' | 'edit';
}

export function NextUpAccompanistModal({
  isOpen,
  clinicId,
  appointment,
  onClose,
  onSaved,
  onDecided,
  context = 'nextUp',
}: NextUpAccompanistModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'ask' | 'select'>('ask');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cadastro rápido do acompanhante não encontrado — abre por cima deste
  // modal, com o nome buscado pré-preenchido.
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerName, setRegisterName] = useState('');

  // Reabre sempre no passo da pergunta (o modal é reutilizado entre marcações).
  useEffect(() => {
    if (isOpen) {
      setStep('ask');
      setError(null);
      setRegisterOpen(false);
    }
  }, [isOpen, appointment?.id]);

  if (!appointment) return null;

  const hasExisting = !!appointment.accompanistId;

  /** Grava o vínculo no agendamento (CPF descriptografado uma única vez —
   *  mesmo tratamento do useAccompanistChange). */
  const linkAccompanist = async (p: Patient) => {
    if (!clinicId || saving) return;
    setSaving(true);
    setError(null);
    try {
      let cpfPlain: string | undefined;
      const raw = p.cpf as string | undefined;
      if (raw) {
        try {
          cpfPlain = /^\d{11}$/.test(raw) ? raw : await decryptData(raw);
        } catch {
          cpfPlain = undefined;
        }
      }
      await AppointmentService.setAccompanist(clinicId, appointment.id, {
        id: p.id,
        name: p.name,
        cpf: cpfPlain,
      });
      onSaved?.();
      onDecided?.();
      onClose();
    } catch (err) {
      console.error('[next-up] vincular acompanhante falhou:', err);
      setError('Não foi possível salvar o acompanhante. Tente de novo.');
    } finally {
      setSaving(false);
    }
  };

  /** "Entra sozinho": remove acompanhante já informado (se houver) e SEMPRE
   *  grava — mesmo sem acompanhante prévio o setAccompanist(null) carimba o
   *  `accompanistAsked`, que evita a re-pergunta no "Avisar médico". */
  const handleAlone = async () => {
    if (saving) return;
    if (!clinicId) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await AppointmentService.setAccompanist(clinicId, appointment.id, null);
      onSaved?.();
      onDecided?.();
      onClose();
    } catch (err) {
      console.error('[next-up] remover acompanhante falhou:', err);
      setError('Não foi possível remover o acompanhante. Tente de novo.');
    } finally {
      setSaving(false);
    }
  };

  /** Volta do cadastro rápido: vincula o recém-criado e (se pedido) abre a
   *  ficha completa na aba Cadastro — o vínculo já fica gravado antes. */
  const handleRegistered = async (
    patientId: string,
    opts?: { openTab?: string },
  ) => {
    setRegisterOpen(false);
    if (!clinicId) return;
    try {
      const p = await PatientService.getPatient(clinicId, patientId);
      if (p) await linkAccompanist({ ...p, id: patientId });
    } catch (err) {
      console.error('[next-up] carregar acompanhante recém-criado falhou:', err);
      setError('Acompanhante cadastrado, mas não foi possível vinculá-lo. Busque pelo nome.');
    }
    if (opts?.openTab) {
      navigate(`/pacientes/${patientId}?tab=${opts.openTab}`);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={context === 'edit' ? 'Acompanhante do dia' : 'Próximo a ser atendido'}
        maxWidth="sm"
      >
        <div className="space-y-4">
          {context === 'edit' ? (
            <p className="text-sm text-on-surface flex items-start gap-2">
              <Users size={16} className="text-primary shrink-0 mt-0.5" />
              <span>
                <strong>{appointment.patientName || 'O paciente'}</strong> vai entrar{' '}
                <strong>acompanhado</strong> hoje?
              </span>
            </p>
          ) : (
            <p className="text-sm text-on-surface flex items-start gap-2">
              <Star size={16} className="text-amber fill-amber shrink-0 mt-0.5" />
              <span>
                <strong>{appointment.patientName || 'O paciente'}</strong> foi marcado
                como o próximo a ser atendido. Vai entrar <strong>acompanhado</strong>?
              </span>
            </p>
          )}

          {error && (
            <p className="text-xs text-error flex items-center gap-1.5">
              <AlertCircle size={13} className="shrink-0" /> {error}
            </p>
          )}

          {step === 'ask' && (
            <div className="space-y-2">
              {hasExisting && (
                <div className="rounded-lg bg-primary-container/40 border border-primary/20 px-3 py-2 text-xs text-on-surface flex items-center gap-2">
                  <Users size={13} className="text-primary shrink-0" />
                  Acompanhante já informado:{' '}
                  <strong className="truncate">{appointment.accompanistName}</strong>
                </div>
              )}
              <button
                type="button"
                onClick={() => setStep('select')}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-60"
              >
                <Users size={15} />
                {hasExisting ? 'Sim — trocar o acompanhante' : 'Sim, vai entrar acompanhado'}
              </button>
              {hasExisting && (
                <button
                  type="button"
                  onClick={() => {
                    onDecided?.();
                    onClose();
                  }}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-outline-variant/40 bg-surface-container text-on-surface text-sm font-medium hover:bg-surface-container-high transition-colors disabled:opacity-60"
                >
                  <Users size={15} />
                  Sim — manter {appointment.accompanistName || 'o acompanhante'}
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleAlone()}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-outline-variant/40 bg-surface-container text-on-surface text-sm font-medium hover:bg-surface-container-high transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <UserX size={15} />}
                Não, entra sozinho
              </button>
            </div>
          )}

          {step === 'select' && (
            /* Altura mínima reserva espaço pro dropdown de resultados dentro
               do miolo rolável do Modal. */
            <div className="space-y-3 min-h-[300px]">
              {saving ? (
                <p className="text-sm text-on-surface-variant flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Salvando acompanhante…
                </p>
              ) : (
                <AccompanistSelect
                  clinicId={clinicId}
                  onChange={(p) => {
                    if (p) void linkAccompanist(p);
                  }}
                  excludePatientId={appointment.patientId}
                  label="Quem é o acompanhante?"
                  onRegisterNew={(q) => {
                    setRegisterName(q);
                    setRegisterOpen(true);
                  }}
                />
              )}
              <button
                type="button"
                onClick={() => setStep('ask')}
                disabled={saving}
                className="text-[11px] text-on-surface-variant hover:text-on-surface"
              >
                Voltar
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Cadastro rápido do acompanhante (exige nome completo, CPF e telefone)
          — renderizado DEPOIS do Modal pra empilhar por cima (mesmo z-50,
          ordem no DOM). */}
      <PatientFormModal
        isOpen={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSuccess={(patientId, opts) => void handleRegistered(patientId, opts)}
        initialName={registerName}
        requireFullIdentity
        title="Cadastrar acompanhante"
      />
    </>
  );
}
